import Link from 'next/link'
import { Logo } from './Logo'
import { RailNav } from './RailNav'
import { FeedbackMenu } from './FeedbackMenu'
import { ModeToggle } from './ModeToggle'
import { ThemeMenu } from './ThemeMenu'
import { Icon } from '@/components/ui/Icon'
import { Popover } from '@/components/ui/Popover'
import { ROLE_LABELS, type WorkspaceRole } from '@/lib/roles'
import { cx } from '@/lib/ui'

type WorkspaceLink = { id: string; name: string; slug: string; role: WorkspaceRole }

function initials(name: string) {
  const words = name.trim().split(/\s+/).filter(Boolean)
  const pair = words.length >= 2 ? words[0][0] + words[1][0] : name.slice(0, 2)
  return pair.toUpperCase()
}

const summaryReset = 'cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden'

/** Opens upward from the bottom bar on phones, to the right of the rail otherwise. */
const popover =
  'glass absolute bottom-full left-0 z-40 mb-3 w-64 rounded-2xl p-1.5 sm:bottom-auto sm:left-full sm:top-0 sm:mb-0 sm:ml-3'
/**
 * The last two controls on the bottom bar, so on a phone their menus anchor to
 * the RIGHT edge. Anchored left they ran off the side of the screen — the
 * account button starts around x=326 on a 390px phone, and a 256px menu from
 * there ends at 582px — taking sign out and the whole feedback form with them.
 * Above sm the rail is vertical and they open beside it as before.
 */
const popoverBottom =
  'glass absolute bottom-full right-0 z-40 mb-3 w-64 max-w-[calc(100vw-1.5rem)] rounded-2xl p-1.5 sm:right-auto sm:left-full sm:bottom-0 sm:mb-0 sm:ml-3'

const menuItem =
  'flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-sm transition-colors hover:bg-hover'

/**
 * The workspace chrome: a frosted icon rail down the left on laptops, a
 * floating bar along the bottom on phones. Holds the mark, the workspace
 * switcher, the four sections and the account menu.
 */
export function WorkspaceRail({
  current,
  role,
  workspaces,
  userEmail,
  isPlatformAdmin,
}: {
  current: { name: string; slug: string }
  role: WorkspaceRole
  workspaces: WorkspaceLink[]
  userEmail: string
  isPlatformAdmin: boolean
}) {
  const base = `/w/${current.slug}`

  return (
    <div className="fixed inset-x-3 bottom-3 z-30 print:hidden sm:sticky sm:inset-auto sm:top-0 sm:h-dvh sm:shrink-0 sm:p-3">
      <aside className="glass flex items-center justify-between gap-1 rounded-[1.5rem] px-2 py-1.5 sm:h-full sm:w-[4.5rem] sm:flex-col sm:justify-start sm:gap-3 sm:px-0 sm:py-4">
        <div className="hidden sm:block">
          <Logo compact href={`${base}/projects`} />
        </div>

        <Popover
          summaryClass={cx(
            summaryReset,
            'gradient-pastel grid h-11 w-11 place-items-center rounded-2xl text-[11px] font-semibold tracking-wide text-foreground/80 shadow-[var(--shadow-card)] ring-1 ring-[var(--glass-rim)] transition-transform hover:scale-[1.04]',
          )}
          className={popover}
          summary={
            <span title={`${current.name} · ${ROLE_LABELS[role]}`} aria-label="Switch workspace">
            {initials(current.name)}
            </span>
          }
        >
            <p className="px-2.5 pb-1 pt-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-muted">
              Workspaces
            </p>
            {workspaces.map((workspace) => {
              const active = workspace.slug === current.slug
              return (
                <Link
                  key={workspace.id}
                  href={`/w/${workspace.slug}/projects`}
                  className={cx(menuItem, active && 'bg-hover')}
                >
                  <span className="gradient-pastel grid h-7 w-7 shrink-0 place-items-center rounded-lg text-[9px] font-semibold text-foreground/70">
                    {initials(workspace.name)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{workspace.name}</span>
                    <span className="block text-[10px] uppercase tracking-wide text-muted">
                      {ROLE_LABELS[workspace.role]}
                    </span>
                  </span>
                  {active && <Icon name="check" className="h-4 w-4 text-foreground" />}
                </Link>
              )
            })}
            <Link href="/onboarding" className={cx(menuItem, 'mt-1 text-xs text-muted hover:text-foreground')}>
              <Icon name="plus" className="h-4 w-4" />
              New workspace
            </Link>
        </Popover>

        <RailNav base={base} />

        {/* Above the account menu and below the sections: reachable from every
            page, and not competing with the five things people came to do. */}
        <div className="sm:mt-auto">
          <FeedbackMenu
            summaryClass={cx(
              summaryReset,
              'grid h-11 w-11 place-items-center rounded-2xl text-muted transition-colors hover:bg-hover hover:text-foreground',
            )}
            popoverClass={popoverBottom}
          />
        </div>

        <Popover
          summaryClass={cx(
            summaryReset,
            'grid h-11 w-11 place-items-center rounded-2xl text-muted transition-colors hover:bg-hover hover:text-foreground',
          )}
          className={popoverBottom}
          summary={
            <span aria-label="Account" title={userEmail}>
              <Icon name="user" className="h-5 w-5" />
            </span>
          }
        >
            <p className="truncate px-2.5 pb-1.5 pt-1.5 text-xs text-muted" title={userEmail}>
              {userEmail}
            </p>
            <ModeToggle />
            <ThemeMenu />

            {/* Only rendered for platform admins. The page itself 404s for
                everyone else — this just saves them finding out. */}
            {isPlatformAdmin && (
              <Link href="/admin" className={menuItem}>
                <Icon name="settings" className="h-4 w-4" />
                Admin
              </Link>
            )}
            <form method="post" action="/api/auth/logout">
              <button type="submit" className={menuItem}>
                <Icon name="logout" className="h-4 w-4" />
                Sign out
              </button>
            </form>
        </Popover>
      </aside>
    </div>
  )
}
